import { useState } from "react";
import { toast } from "sonner";
import {
  ChevronDown,
  Pencil,
  Power,
  PowerOff,
  Scale,
  Star,
  Trash2,
  type LucideIcon,
} from "lucide-react";
import type { AccountBalance, AccountItem } from "@boo/shared";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import ConfirmModal from "@/components/ConfirmModal";
import ListRowsSkeleton from "@/components/skeletons/ListRowsSkeleton";
import { ApiError } from "@/services/expenseApi";
import { useAccountStore } from "../store/useAccountStore";
import { AccountType } from "@boo/shared";
import { ACCOUNT_TYPE_LABELS } from "../types/accounts.types";
import AccountAvatar from "./AccountAvatar";
import AccountForm from "./AccountForm";
import CheckpointForm from "./CheckpointForm";
import { formatBaht } from "../../bills/lib/billMath";

function IconAction({
  label,
  icon: Icon,
  onClick,
  className = "",
}: {
  label: string;
  icon: LucideIcon;
  onClick: () => void;
  className?: string;
}) {
  return (
    <Button
      variant="ghost"
      size="icon"
      title={label}
      aria-label={label}
      className={`h-9 w-9 sm:h-9 sm:w-9 ${className}`}
      onClick={onClick}
    >
      <Icon className="w-4 h-4" />
    </Button>
  );
}

export default function AccountList() {
  const { accounts, balances, loading, updateAccount, deleteAccount } =
    useAccountStore();
  const [checkpointId, setCheckpointId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<AccountItem | null>(null);
  const [expanded, setExpanded] = useState(false);

  async function run(action: () => Promise<void>) {
    try {
      await action();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Action failed");
    }
  }

  if (loading && accounts.length === 0) {
    return <ListRowsSkeleton rows={2} variant="cards" />;
  }
  if (accounts.length === 0) {
    return (
      <p className="text-sm text-muted-foreground text-center py-10">
        No accounts yet. Add one below.
      </p>
    );
  }

  // Collapsed, only the default account shows (else the first active one).
  const primary =
    accounts.find((a) => a.isDefault && a.isActive) ??
    accounts.find((a) => a.isActive) ??
    accounts[0];
  const visible = expanded ? accounts : [primary];
  const hiddenCount = accounts.length - 1;

  return (
    <>
      <div className="flex flex-col gap-3 sm:gap-4">
        {visible.map((account) => (
          <Card
            key={account.id}
            className={account.isActive ? "" : "opacity-60"}
          >
            <CardContent className="pt-5 pb-4 sm:pt-6 sm:pb-6">
              {editingId === account.id ? (
                <AccountForm
                  account={account}
                  onDone={() => setEditingId(null)}
                />
              ) : (
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="min-w-0 flex items-start gap-3">
                    <AccountAvatar
                      type={account.type}
                      bankCode={account.bankCode}
                      cardNetwork={account.cardNetwork}
                    />
                    <div className="min-w-0 flex flex-col gap-1.5">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-foreground truncate">
                          {account.name}
                        </span>
                        {account.isDefault && <Badge>Default</Badge>}
                        {!account.isActive && (
                          <Badge variant="outline">Inactive</Badge>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground [font-variant-numeric:tabular-nums]">
                        {ACCOUNT_TYPE_LABELS[account.type]}
                        {account.institution && ` · ${account.institution}`}
                        {account.digits && ` · ••${account.digits}`}
                      </div>
                      <BalanceLine
                        type={account.type}
                        balance={balances[account.id]}
                      />
                    </div>
                  </div>
                  <div className="flex w-full flex-wrap gap-1 border-t border-border pt-2 sm:w-auto sm:border-t-0 sm:pt-0">
                    {account.isActive && !account.isDefault && (
                      <IconAction
                        label="Make default"
                        icon={Star}
                        onClick={() =>
                          run(() =>
                            updateAccount(account.id, { isDefault: true }),
                          )
                        }
                      />
                    )}
                    <IconAction
                      label="Set balance"
                      icon={Scale}
                      onClick={() => setCheckpointId(account.id)}
                    />
                    <IconAction
                      label="Edit"
                      icon={Pencil}
                      onClick={() => setEditingId(account.id)}
                    />
                    <IconAction
                      label={account.isActive ? "Deactivate" : "Reactivate"}
                      icon={account.isActive ? PowerOff : Power}
                      onClick={() =>
                        run(() =>
                          updateAccount(account.id, {
                            isActive: !account.isActive,
                          }),
                        )
                      }
                    />
                    {account.isEmpty && (
                      <IconAction
                        label="Delete"
                        icon={Trash2}
                        className="text-red-500 hover:text-red-500"
                        onClick={() => setDeleting(account)}
                      />
                    )}
                  </div>
                </div>
              )}
              {checkpointId === account.id && editingId !== account.id && (
                <CheckpointForm
                  account={account}
                  onDone={() => setCheckpointId(null)}
                />
              )}
            </CardContent>
          </Card>
        ))}
        {hiddenCount > 0 && (
          <button
            type="button"
            onClick={() => setExpanded((open) => !open)}
            aria-expanded={expanded}
            className="inline-flex items-center justify-center gap-1.5 self-center rounded-lg px-3 py-2 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground hover:bg-muted"
          >
            {expanded
              ? "Show only default"
              : `Show ${hiddenCount} more ${hiddenCount === 1 ? "account" : "accounts"}`}
            <ChevronDown
              className={`w-3.5 h-3.5 transition-transform ${expanded ? "rotate-180" : ""}`}
            />
          </button>
        )}
      </div>

      <ConfirmModal
        open={deleting !== null}
        title="Delete account"
        message={`Delete "${deleting?.name}"? This can't be undone.`}
        confirmLabel="Delete"
        danger
        onCancel={() => setDeleting(null)}
        onConfirm={() => {
          const target = deleting;
          setDeleting(null);
          if (target) void run(() => deleteAccount(target.id));
        }}
      />
    </>
  );
}

function BalanceLine({
  type,
  balance,
}: {
  type: AccountType;
  balance: AccountBalance | undefined;
}) {
  const owed = type === AccountType.CreditCard;
  if (!balance || balance.balance === null) {
    return (
      <div className="text-xs text-muted-foreground">
        No balance yet. Set one to start tracking
      </div>
    );
  }
  const difference = balance.unexplainedDifference;
  return (
    <div className="[font-variant-numeric:tabular-nums]">
      <span className="text-base font-semibold text-foreground">
        {formatBaht(balance.balance)}
      </span>
      <span className="text-xs text-muted-foreground ml-2">
        {owed ? "owed · " : ""}since {balance.checkpointDate}
      </span>
      {difference !== null && difference !== 0 && (
        <div className="text-xs text-amber-600">
          Unexplained difference {difference > 0 ? "+" : ""}
          {formatBaht(difference)} at the last balance check
        </div>
      )}
    </div>
  );
}
